import { useEffect, useRef, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import Pagination, { usePagination } from '../components/Pagination';
import EnrolmentDrawer from '../components/EnrolmentDrawer';
import { fmtWaiting, fmtDT } from '../utils/format';

/* ICIB-03 — Track outstanding enrolments and incomplete applications.
   The queue (GET /notifications?company={gid}&include_resolved=true —
   ICIB-03a's documented `group` filter plus its `include_resolved` one)
   is scoped to the currently-selected group, same as every other list
   here. Shows every notification this group has ever raised, not just
   the open ones — resolving one (ICIB-03e) marks it resolved rather than
   removing it from view, so admin can still see what was actioned and
   when, not just what's currently outstanding. Each row carries
   `employee_name` directly (added to ICIB-03a's contract) so the row is
   readable at a glance without waiting on a drill-down — but that's the
   only person-level detail it carries; the enrolment_id still needs the
   drill-down (GET /enrolments/{id}, ICIB-03b) for everything else (email,
   product, group, employee_id), fetched on demand (via loadEnrolmentDetail,
   cached in IcibAdminContext) only when the user selects that
   notification, rather than eagerly for the whole queue. Selecting a row
   opens a drawer with everything ICIB-03b returns. */
const reasonLabel = (n) => (n.type === 'stale_invitation' ? 'Invitation not actioned' : 'Removal not confirmed by insurer');

function FollowupView() {
  const {
    status, groupId, curGroup, allNotifs, findEnrolment, loadEnrolmentDetail,
    resendInvite, resendAll, resolveNotification, rateLimited, product, group,
    notificationsStatus, notificationsError, loadNotifications,
  } = useIcibAdmin();
  const { openDrawer } = useDrawer();
  const toast = useToast();
  const [busyId, setBusyId] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const selectRequestRef = useRef(0);
  const [q, setQ] = useState('');

  useEffect(() => { loadNotifications(groupId); }, [groupId, loadNotifications]);

  const queue = allNotifs();
  // Searches what each row shows before any drill-down — the person's name
  // and the "Why it's here" text. Email only appears once a row's ICIB-03b
  // detail has loaded, so it isn't searchable reliably and is left out.
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? queue.filter((n) => `${n.employee_name || ''} ${reasonLabel(n)} ${n.message || ''}`.toLowerCase().includes(needle))
    : queue;
  const { page, setPage, pageCount, pageItems: pagedQueue, total } = usePagination(shown);

  async function onSelect(n) {
    setSelectedId(n.id);
    // Always refetch rather than reuse enrolmentDetails' cached copy — that
    // cache is filled long before a click (Overview prefetches every open
    // notification's enrolment; an earlier click here or in Confirmations
    // fills it too) and nothing invalidates it when the enrolment changes.
    // Removing a product from someone with an open stale invitation used to
    // open their new removal task's drawer still showing `invited` until a
    // page reload. The cached copy still serves findEnrolment's row email.
    const requestId = ++selectRequestRef.current;
    const detail = await loadEnrolmentDetail(n.enrolment_id);
    // A later click superseded this one while it was in flight.
    if (selectRequestRef.current !== requestId) return;
    if (detail?.error) { toast(detail.message || 'Could not load this enrolment.', 'bad', detail.error); return; }
    const person = detail.employee?.user;
    const note = <><b>{reasonLabel(n)}</b>{n.message}</>;
    openDrawer(
      person ? `${person.first_name} ${person.last_name}` : 'Enrolment detail',
      `ICIB-03b · GET /enrolments/${n.enrolment_id}`,
      <EnrolmentDrawer detail={detail} note={note} product={product} group={group} />
    );
  }

  async function onResend(n) {
    setBusyId(n.id);
    const res = await resendInvite(n.enrolment_id);
    setBusyId(null);
    if (res?.error) {
      toast(res.message || 'Could not resend this reminder.', 'bad', res.error);
      // 409 invalid_state_transition: the invitation was closed after this
      // page loaded (e.g. onboarding recorded elsewhere), so the backend
      // refused the resend. Reload the queue so the row shows as Resolved
      // and its Resend button disables.
      if (res.error === 'invalid_state_transition') loadNotifications(groupId);
    } else toast('Reminder queued.', 'ok');
  }

  async function onResolve(n) {
    setBusyId(n.id);
    const res = await resolveNotification(n.id);
    setBusyId(null);
    if (res?.error) toast(res.message || 'Could not resolve this notification.', 'bad', res.error);
    else toast('Marked resolved.', 'ok');
  }

  async function onResendAll() {
    if (rateLimited()) { toast('Too many resend requests. Limit is 10 per minute.', 'bad', 'rate_limited'); return; }
    setSweeping(true);
    const res = await resendAll(groupId);
    setSweeping(false);
    // The sweep itself doesn't resolve anything (README §4.8), but this page
    // may be stale — tasks can have closed since it loaded (e.g. someone was
    // onboarded elsewhere, which is also why the backend skips them). Reload
    // the queue so those rows flip to Resolved and their buttons disable.
    loadNotifications(groupId);
    if (res?.error) { toast(res.message || 'Could not resend outstanding invitations.', 'bad', res.error); return; }
    const failedRows = (res.results || []).filter((r) => r.status === 'error');
    toast(`${res.resent_count || 0} reminder(s) queued${failedRows.length ? `, ${failedRows.length} skipped` : ''}.`, failedRows.length ? 'bad' : 'ok');
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Outstanding enrolments</div>
      <div className="page-h"><h2>Follow-up tasks</h2></div>
      <p className="page-sub">What the system has flagged for {curGroup()?.name || 'this company'} — stale invitations and unconfirmed removals, open and already-resolved alike.</p>

      {notificationsError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load the notification queue</b>{notificationsError}</div>}

      <div className="filterbar">
        <input type="search" placeholder="Search name or reason…" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="spacer" style={{ flex: 1 }} />
        <button className="btn" onClick={onResendAll} disabled={sweeping || !groupId || !queue.some((n) => n.type === 'stale_invitation' && !n.resolved_at)}>
          {sweeping ? 'Sweeping…' : 'Resend every stale invitation'}
        </button>
        <span className="ep">GET /notifications?company={'{company}'}&amp;include_resolved=true</span>
      </div>

      {!groupId ? (
        <div className="card"><div className="empty"><b>Select a company</b>Choose a company from the scope selector above.</div></div>
      ) : (
        <div className="card">
          {notificationsStatus === 'loading' || notificationsStatus === 'idle' ? (
            <div className="empty"><b>Loading queue…</b></div>
          ) : shown.length ? (
            <div className="tbl-wrap">
              <table>
                <thead><tr><th>Person</th><th>Why it's here</th><th>Waiting</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {pagedQueue.map((n) => {
                    const en = findEnrolment(n.enrolment_id);
                    const busy = busyId === n.id;
                    const selected = selectedId === n.id;
                    const resolved = !!n.resolved_at;
                    return (
                      <tr key={n.id} className="clickable" aria-selected={selected} onClick={() => onSelect(n)}>
                        <td>
                          <span className="strong">{n.employee_name || 'Unknown'}</span><br />
                          {en ? (
                            <span className="mono dim">{en.emp.user?.email}</span>
                          ) : (
                            <span className="dim">{selected ? 'Loading full detail…' : 'Select for full detail'}</span>
                          )}
                        </td>
                        <td>
                          {reasonLabel(n)}
                          <br /><span className="mono dim">{n.message}</span>
                        </td>
                        <td className="mono nowrap">{fmtWaiting(n)}<br /><span className="dim">Flagged {fmtDT(n.created_at)}</span></td>
                        <td>
                          <span className={'tag' + (resolved ? '' : ' warn')}>{resolved ? 'Resolved' : 'Open'}</span>
                          {resolved && <div className="dim" style={{ marginTop: 4 }}>{fmtDT(n.resolved_at)}</div>}
                        </td>
                        <td>
                          <div className="btn-row">
                            {n.type === 'stale_invitation' && (
                              <button className="btn sm" disabled={busy || resolved || !en} onClick={(e) => { e.stopPropagation(); onResend(n); }}>{busy ? '…' : 'Resend'}</button>
                            )}
                            <button className="btn sm" disabled={busy || resolved} onClick={(e) => { e.stopPropagation(); onResolve(n); }}>{resolved ? 'Resolved' : 'Resolve'}</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : queue.length ? (
            <div className="empty"><b>No matches</b>Try a different search.</div>
          ) : (
            <div className="empty"><b>Nothing here yet</b>No invitation or removal for this company has ever been flagged.</div>
          )}
          <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
        </div>
      )}
    </>
  );
}

export default FollowupView;
